import React from 'react';
import { Box, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/hooks';

interface StyledBannerBoxProps {
  isMobile?: boolean;
}

const StyledBannerBox = styled(Box)<StyledBannerBoxProps>(({ isMobile = false }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: isMobile ? '222px' : '106px',
  maxWidth: '100%',
  marginBottom: '10px',
  borderRadius: '5px',
  background:
    'linear-gradient(175.46deg, rgba(255, 228, 159, 0.25) -9.81%, rgba(254, 178, 30, 0.25) 96.32%)',
  border: '1px solid',
  borderImageSource: 'linear-gradient(175.46deg, #FFE49F -9.81%, #FEB21E 96.32%)',
  borderImageSlice: 1,
  padding: isMobile ? '16px 24px 16px 24px' : 0,
}));

interface IWarningBannerProps {
  username: string;
  noAvailableSlots: boolean;
  isProvideOnlineConsultations?: boolean;
}

export default function WarningBanner({
  username,
  noAvailableSlots = false,
  isProvideOnlineConsultations,
}: IWarningBannerProps) {
  const { t } = useTranslation();
  const { calendar, videoCallPlatform } = useAppSelector(
    (state) => state.miniweb.coachPublicDetails
  );
  const { getPublicDetailsLoading } = useAppSelector((state) => state.miniweb);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showBanner =
    noAvailableSlots ||
    calendar === null ||
    (videoCallPlatform === null && isProvideOnlineConsultations === true);

  const getBannerContent = () => {
    if (noAvailableSlots) {
      return {
        title: t('miniWebSite.error.noAvailabilities', { username }),
        description: t('miniWebSite.error.noAvailabilitiesDesc', { username }),
      };
    }
    if (calendar === null && videoCallPlatform === null) {
      return {
        title: t('miniWebSite.error.noCalendarAndVideoCallPlatform', { username }),
        description: t('miniWebSite.error.noCalendarAndVideoCallPlatformDesc', { username }),
      };
    }
    if (calendar === null) {
      return {
        title: t('miniWebSite.error.noCalendar', { username }),
        description: t('miniWebSite.error.noCalendarDesc', { username }),
      };
    }
    if (videoCallPlatform === null) {
      return {
        title: t('miniWebSite.error.noVideoCallPlatform', { username }),
        description: t('miniWebSite.error.noVideoCallPlatformDesc', { username }),
      };
    }
    return {
      title: '',
      description: '',
    };
  };

  const { title, description } = getBannerContent();

  if (!showBanner) {
    return null;
  }

  if (getPublicDetailsLoading) {
    return null;
  }

  return (
    <Box>
      <StyledBannerBox isMobile={isMobile}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          height="100%"
          fontWeight="700"
          lineHeight="18px"
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            p="0px 10px 0 15px"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              {...(isMobile && {
                sx: {
                  alignItems: 'center',
                  gap: '10px',
                },
              })}
            >
              <Box paddingBottom="5px">
                <img
                  src="/assets/icons/shield-warning-yellow.svg"
                  width={40}
                  alt="shield-warning-yellow"
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                textAlign={isMobile ? 'center' : 'left'}
                gap={isMobile ? '10px' : '0'}
              >
                <Box display="flex" flexDirection="row" p="0px 10px 5px 10px">
                  <Typography variant="bodyBold" color="primary">
                    {title}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="row" justifyContent="center" pl="10px">
                  <Typography variant="bodyRegular" color="primary" fontSize={16}>
                    {description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </StyledBannerBox>
    </Box>
  );
}
